import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ToggleButton from '@mui/material/ToggleButton';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CallIcon from '@mui/icons-material/Call';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import { MapContainer, TileLayer, CircleMarker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import api from '../api/client';
import useLocation from '../hooks/useLocation';
import { formatMoney } from '../components/Money';

/**
 * Nearby available workers for constructors — OpenStreetMap (no API key)
 * with our own haversine search underneath.
 */
const FindWorkers = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const { capture, saved } = useLocation();

  const [center, setCenter] = useState(saved);
  const [radius, setRadius] = useState(25);
  const [workers, setWorkers] = useState(null);
  const [locating, setLocating] = useState(false);

  const search = async (loc, km) => {
    if (!loc) return;
    const { data } = await api.get('/api/workers/nearby', {
      params: { lat: loc.lat, lng: loc.lng, radiusKm: km },
    });
    setWorkers(data);
  };

  const locate = async () => {
    setLocating(true);
    const loc = await capture();
    setLocating(false);
    if (loc) {
      setCenter(loc);
      search(loc, radius);
    }
  };

  useEffect(() => {
    if (saved) search(saved, radius);
    else locate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const changeRadius = (_, km) => {
    if (!km) return;
    setRadius(km);
    search(center, km);
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <IconButton onClick={() => navigate('/')} edge="start">
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          {t('nearby.findWorkers')}
        </Typography>
        <IconButton onClick={locate} disabled={locating} aria-label={t('nearby.useLocation')}>
          <MyLocationIcon />
        </IconButton>
      </Stack>

      {!center ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              {t('nearby.locationNeeded')}
            </Typography>
            <Button variant="contained" startIcon={<MyLocationIcon />} disabled={locating} onClick={locate}>
              {t('nearby.useLocation')}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              {t('nearby.radius')}
            </Typography>
            <ToggleButtonGroup size="small" exclusive value={radius} onChange={changeRadius}>
              {[5, 10, 25, 50].map((km) => (
                <ToggleButton key={km} value={km} sx={{ px: 1.5 }}>
                  {km} km
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Stack>

          <Card sx={{ mb: 2, overflow: 'hidden' }}>
            <Box sx={{ height: 280, '& .leaflet-container': { height: '100%', width: '100%' } }}>
              <MapContainer center={[center.lat, center.lng]} zoom={12} scrollWheelZoom={false}>
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <CircleMarker
                  center={[center.lat, center.lng]}
                  radius={9}
                  pathOptions={{ color: '#fff', weight: 2, fillColor: theme.palette.secondary.main, fillOpacity: 1 }}
                >
                  <Popup>{t('nearby.you')}</Popup>
                </CircleMarker>
                {(workers || []).map((w) => (
                  <CircleMarker
                    key={w.id}
                    center={[Number(w.lat), Number(w.lng)]}
                    radius={9}
                    pathOptions={{ color: '#fff', weight: 2, fillColor: theme.palette.primary.main, fillOpacity: 1 }}
                  >
                    <Popup>
                      <strong>{w.name}</strong>
                      <br />
                      {w.skill || ''} {w.daily_rate ? `· ${formatMoney(w.daily_rate)}/day` : ''}
                      <br />
                      <a href={`tel:${w.phone}`}>{w.phone}</a>
                    </Popup>
                  </CircleMarker>
                ))}
              </MapContainer>
            </Box>
          </Card>

          {workers === null ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : workers.length === 0 ? (
            <Alert severity="info">
              {t('nearby.noneFound')} — {t('nearby.noneFoundHint')}
            </Alert>
          ) : (
            <Stack spacing={1.5}>
              {workers.map((w) => (
                <Card key={w.id}>
                  <CardContent sx={{ '&:last-child': { pb: 2 } }}>
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                      <Avatar sx={{ bgcolor: 'primary.main', color: 'primary.contrastText' }}>
                        {w.name?.[0]?.toUpperCase() || '?'}
                      </Avatar>
                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Typography variant="subtitle2" noWrap>
                          {w.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {w.skill ? `${w.skill} · ` : ''}
                          {w.daily_rate ? `${formatMoney(w.daily_rate)}/day · ` : ''}
                          {t('nearby.kmAway', { km: w.distance_km })}
                        </Typography>
                      </Box>
                      <Chip
                        size="small"
                        color="success"
                        variant="outlined"
                        icon={<CallIcon sx={{ fontSize: 14 }} />}
                        label={t('nearby.call')}
                        component="a"
                        href={`tel:${w.phone}`}
                        clickable
                      />
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          )}
        </>
      )}
    </Box>
  );
};

export default FindWorkers;
